import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import translations from '../i18n/translations';
import {
  applyLanguageToDocument,
  getStoredAppLanguage,
  normalizeLanguageCode,
  setStoredAppLanguage,
} from '../utils/languagePreference';

const LanguageContext = createContext(null);

const getValueByPath = (source, path) => {
  if (!source || !path) return undefined;
  return path.split('.').reduce((value, segment) => {
    if (value && Object.prototype.hasOwnProperty.call(value, segment)) {
      return value[segment];
    }
    return undefined;
  }, source);
};

const interpolate = (value, params = {}) => {
  if (typeof value !== 'string') return value;
  return value.replace(/\{\{(\w+)\}\}/g, (_, token) => {
    if (Object.prototype.hasOwnProperty.call(params, token)) {
      return String(params[token]);
    }
    return '';
  });
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => getStoredAppLanguage());

  useEffect(() => {
    applyLanguageToDocument(language);
  }, [language]);

  const changeLanguage = (nextLanguage) => {
    const normalized = setStoredAppLanguage(nextLanguage);
    setLanguage(normalized);
    return normalized;
  };

  const t = (key, params = {}, fallback = '') => {
    const normalized = normalizeLanguageCode(language);
    const activeLanguageSet = translations[normalized] || {};
    const defaultLanguageSet = translations.en || {};

    const activeValue = getValueByPath(activeLanguageSet, key);
    const defaultValue = getValueByPath(defaultLanguageSet, key);
    const resolved = activeValue ?? defaultValue ?? fallback ?? key;

    return interpolate(resolved, params);
  };

  const value = useMemo(() => ({
    language,
    changeLanguage,
    t,
  }), [language]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
